import { Button, Checkbox, Input, Select } from '@calizahq/react-components-v2'
import { AgentMutations } from '@sales-app/graphql/mutations/agent.mutations'
import { AgentQueries } from '@sales-app/graphql/queries/agent.queries'
import { Agent, Status } from '@sales-app/types/Agent'
import { Form, Formik } from 'formik'
import { debounce } from 'lodash'
import React, { FC, useState } from 'react'

import { InputSelect } from '@/components'
import { graphQLClient, useGraphqlMutation } from '@/hooks/useGraphql'

import { EditAgentFormValuesType } from './EditAgent.util'

type EditAgentFormProps = {
  agent: Agent
  refetchOnSuccess: () => void
  toggle: () => void
}

export const EditAgentForm: FC<EditAgentFormProps> = ({ agent, toggle, refetchOnSuccess }) => {
  const [error, setError] = useState<string>()

  const { mutate: update, isPending: isUpdateLoading } = useGraphqlMutation(AgentMutations.updateAgent(), {
    onSuccess: () => {
      refetchOnSuccess && refetchOnSuccess()
      toggle && toggle()
    },
    onError: () => {
      setError('Error creating new agent')
    },
  })

  const statusOptions = Object.values(Status).map((status) => {
    return { label: status, value: status }
  })

  const debouncedSearchAgent = debounce(searchAgent, 1000, { leading: true })
  const loadAgentOptions = (inputValue) => debouncedSearchAgent(inputValue)

  const roleOptions = [
    { label: 'Agent', value: 'Agent' },
    { label: 'Supervisor', value: 'Supervisor' },
  ]

  const handleUpdate = (values: EditAgentFormValuesType) => {
    update({
      agent: {
        agentIds: [agent.id],
        corporateHousing: values.corporateHousing,
        dialpadUserId: values.dialpadUserId,
        role: values.role,
        managerUserId: values.supervisorId ? values.supervisorId : null,
        standard: values.standard,
        status: values.status,
      },
    })
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          agentIds: agent && [agent.id],
          corporateHousing: agent && agent.corporateHousing,
          dialpadUserId: agent && agent.dialpadUserId,
          supervisorId: agent && agent.supervisor && agent.supervisor.userId,
          role: agent && agent.role,
          standard: agent && agent.standard,
          status: agent && agent.currentStatus,
        }}
        onSubmit={handleUpdate}
      >
        {() => {
          return (
            <Form className="flex w-full flex-col flex-wrap px-6 md:max-w-120">
              <p className="mb-10 text-center font-bold text-header-md leading-none">{agent.name}</p>
              <InputSelect
                async={true}
                className="mb-4 w-full"
                defaultOptions={false}
                id="supervisorId"
                label="Supervisor"
                loadOptions={loadAgentOptions}
                name="supervisorId"
                nullLabel="---"
                optionValue={
                  agent && agent.supervisor
                    ? { label: agent?.supervisor?.name || '', value: agent?.supervisor?.userId }
                    : undefined
                }
                overviewDropdown={true}
              />
              <Select className="mb-4 w-full" id="role" label="Role" name="role" options={roleOptions || null} />
              <Select
                className="mb-4 w-full"
                id="status"
                label="Current Status"
                name="status"
                options={statusOptions}
              />
              <Input className="w-full mb-4" id="dialpadUserId" label="Dialpad User ID" name="dialpadUserId" />
              <Checkbox className="w-full mb-4" id="standard" label="Standard" name="standard" />
              <Checkbox
                className="w-full mb-4"
                id="corporateHousing"
                label="Corporate Housing"
                name="corporateHousing"
              />
              <div>{error && <p className=" mb-4 text-p-sm text-text-semantic-error">{error}</p>}</div>
              <div className="mt-4 flex justify-end md:flex-row	">
                <Button
                  className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
                  disabled={isUpdateLoading}
                  isLoading={isUpdateLoading}
                  size="small"
                  type="submit"
                >
                  Update Agent
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

const searchAgent = (inputValue: string) => {
  return graphQLClient
    .request(AgentQueries.getSearchSupervisor(), {
      searchString: inputValue,
    })
    .then((resResponse: AgentQueries.GET_SEARCH_SUPERVISOR.Response) => {
      if (resResponse.agents) {
        return resResponse.agents.map((agent) => ({ label: agent?.name || '', value: agent?.userId }))
      } else {
        return []
      }
    })
}
